/* eslint-disable prefer-arrow-callback */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getUserFacingApiErrorMessage, useGeneralContext, useTranslation, useUserContext } from "utils";

import { DEFAULT_PROJECT_SETTINGS, DEFAULT_SCENARIO_SETTINGS, SCENARIO_LIMITS } from "consts";

import { DroppedMainCrop } from "models";

import { AlertBanner, MainPage } from "components";

import ScenarioAnalysisView from "pages/app/scenario-analysis/ScenarioAnalysisView";
import { DetailedCalculationsTab } from "pages/app/scenario-analysis/components/DetailedCalculationsTab";
import { OutcomeComparison } from "pages/app/scenario-analysis/components/OutcomeComparison";
import { ProjectManagement } from "pages/app/scenario-analysis/components/ProjectManagement";
import { ScenarioAnalysisModals } from "pages/app/scenario-analysis/components/ScenarioAnalysisModals";
import { ScenarioAnalysisTab } from "pages/app/scenario-analysis/components/ScenarioAnalysisTabBar";
import ScenarioBuilder from "pages/app/scenario-analysis/components/ScenarioBuilder";
import { SideBySideSection } from "pages/app/scenario-analysis/components/SideBySideSection";
import { useActiveProjectLifecycle } from "pages/app/scenario-analysis/hooks/useActiveProjectLifecycle";
import { useAutoRecalcAfterHydration } from "pages/app/scenario-analysis/hooks/useAutoRecalcAfterHydration";
import { useChartScrollOnRecalcStart } from "pages/app/scenario-analysis/hooks/useChartScrollOnRecalcStart";
import { useFieldImportLandingScroll } from "pages/app/scenario-analysis/hooks/useFieldImportLandingScroll";
import { useFieldImportLandingState } from "pages/app/scenario-analysis/hooks/useFieldImportLandingState";
import { useFieldReselect } from "pages/app/scenario-analysis/hooks/useFieldReselect";
import { useModalState } from "pages/app/scenario-analysis/hooks/useModalState";
import { usePostHydrationLatch } from "pages/app/scenario-analysis/hooks/usePostHydrationLatch";
import { useProjectSettingsSave } from "pages/app/scenario-analysis/hooks/useProjectSettingsSave";
import { useRecalcValidation } from "pages/app/scenario-analysis/hooks/useRecalcValidation";
import { DetailedCalcsLeaveGuard, useScenarioAnalysisTabs } from "pages/app/scenario-analysis/hooks/useScenarioAnalysisTabs";
import { useScenarioEditing } from "pages/app/scenario-analysis/hooks/useScenarioEditing";
import { useScenarioPdfExport } from "pages/app/scenario-analysis/hooks/useScenarioPdfExport";
import { useScenarioRecalc } from "pages/app/scenario-analysis/hooks/useScenarioRecalc";
import { useScenarioResilienceScores } from "pages/app/scenario-analysis/hooks/useScenarioResilienceScores";
import { useScenarioScroll } from "pages/app/scenario-analysis/hooks/useScenarioScroll";
import { useScenarioUpdateTracking } from "pages/app/scenario-analysis/hooks/useScenarioUpdateTracking";
import { useScrollIntents } from "pages/app/scenario-analysis/hooks/useScrollIntents";
import { useSoilSightPersistence } from "pages/app/scenario-analysis/hooks/useSoilSightPersistence";
import { useLeaveProjectWithFlush, useReconcileActiveProjectWithProjectList } from "pages/app/scenario-analysis/hooks/useSoilSightProjectSession";
import {
  type ForecastControls,
  type ProjectRefinementsState,
  type ScenarioBuilderHandle,
  type ScenarioData,
  type ScenarioSettings,
} from "pages/app/scenario-analysis/types/scenario";
import { ViewMode } from "pages/app/scenario-analysis/utils/forecast";
import { getForecastPrimaryAction, getPageLoadState, getRecalcValidationErrors } from "pages/app/scenario-analysis/utils/scenarioAnalysisHelpers";
import { toApiSettingsRefinements } from "pages/app/scenario-analysis/utils/scenarioApiMappers";
import { buildDefaultScenarios, withEmptyYearsPruned } from "pages/app/scenario-analysis/utils/scenarioBuilderState";
import { resolveComparisonSelection } from "pages/app/scenario-analysis/utils/scenarioComparison";
import {
  areScenariosReadyForRecalculation,
  getReferenceScenario,
  referenceIsReadyToCalculate,
  referenceYearsAreComplete,
} from "pages/app/scenario-analysis/utils/scenarioValidation";

export const ScenarioAnalysis = () => {
  const { t } = useTranslation(["scenarioAnalysis", "meta"]);
  const { user } = useUserContext();
  const { setNavigationCrumbs } = useGeneralContext();
  const klimFarmId = user?.farm?.id ?? "";

  const landing = useFieldImportLandingState();
  const {
    isAwaitingProjectChoice,
    importedFieldCropYearsRef,
    importedSelectedFieldIdRef,
    fieldImportAlreadyPersistedRef,
    projectEntryDismissed,
    clearImportRouterState,
  } = landing;

  const [settings, setSettings] = useState<ScenarioSettings>(DEFAULT_SCENARIO_SETTINGS);
  const [isDirty, setIsDirty] = useState(false);
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;
  const [scenarios, setScenarios] = useState<ScenarioData[]>(() => {
    const defaults = buildDefaultScenarios(t("scenarioBuilder.reference"));
    const years = importedFieldCropYearsRef.current;
    if (!years?.length) return defaults;
    return defaults.map((s) => (s.isReference ? { ...s, years } : s));
  });
  const [hasCalculated, setHasCalculated] = useState(false);
  // Seeded from the import so the loader shows before the post-hydration run starts; cleared when it starts or is skipped.
  const [awaitingInitialCalc, setAwaitingInitialCalc] = useState(landing.awaitingCalculationOnLanding);
  const { openModals, keyInsightsScenarioId, openModal, closeModal, openKeyInsights } = useModalState();
  const [comparisonScenarioId, setComparisonScenarioId] = useState<string | null>(null);
  // A fresh object per click, so a card clicked twice re-applies its view even if nothing changed.
  const [chartViewRequest, setChartViewRequest] = useState<{ view: ViewMode; scenarioId?: string } | null>(null);
  const handleChartViewRequestHandled = useCallback(function handleChartViewRequestHandled() {
    setChartViewRequest(null);
  }, []);
  // Mirrors the chart's own view so the key insight cards can mark the one whose figure is on show.
  const [activeChartView, setActiveChartView] = useState<ViewMode>("cumulative");

  const scenarioBuilderRef = useRef<ScenarioBuilderHandle>(null);
  // The tab hook needs the recalculation, and the recalculation's scroll and reveal need the tab: these mirror it, set after it runs.
  const activeTabRef = useRef<ScenarioAnalysisTab>("worth-it");
  const goToTabRef = useRef<((tab: ScenarioAnalysisTab, afterPaint?: () => void) => void) | null>(null);
  const [showTransitionModal, setShowTransitionModal] = useState(landing.showTransitionOnLanding);
  const { transitionCropRotationLoaded, transitionImportReadyForRecalc } = landing;
  // Crops the import dropped; the banner shows until the user edits the rotation or dismisses it, for this visit.
  const [droppedMainCrops, setDroppedMainCrops] = useState<DroppedMainCrop[]>(landing.droppedMainCropsOnLanding);
  const [droppedCropsBannerDismissed, setDroppedCropsBannerDismissed] = useState(false);
  const showDroppedCropsBanner = droppedMainCrops.length > 0 && !droppedCropsBannerDismissed;

  const scroll = useScenarioScroll({
    scenarios,
    scenarioBuilderRef,
    fieldImportAlreadyPersistedRef: fieldImportAlreadyPersistedRef,
    showTransitionModal,
  });
  const {
    builderRef,
    chartRef,
    scrollToScenarioCard,
    scrollToFirstIncompleteScenario,
    scrollToBuilder,
    scrollToChart,
    scrollToEconomics,
    economicsRef,
    isChartInView,
  } = scroll;
  const scrollIntents = useScrollIntents({
    scrollToChart,
    activeTabRef,
    isLandingTransitionOpen: fieldImportAlreadyPersistedRef.current && showTransitionModal,
  });

  const [projectRefinements, setProjectRefinements] = useState<ProjectRefinementsState>({
    location: { fieldSelect: importedSelectedFieldIdRef.current ?? null },
    variableCosts: DEFAULT_PROJECT_SETTINGS.variableCosts,
    modelSettings: DEFAULT_PROJECT_SETTINGS.modelSettings,
  });

  const forecastControls = useMemo<ForecastControls>(
    () => ({ marketPriceTrend: settings.market, costDevelopment: settings.costs }),
    [settings.market, settings.costs]
  );

  const drawerSettings = useMemo(() => ({ forecastControls, ...projectRefinements }), [forecastControls, projectRefinements]);

  // Last saved payload per scenario and for the settings, so autosave skips unchanged writes.
  const cropYearsSavedRef = useRef<Map<string, string>>(new Map());
  const scenarioMetaSavedRef = useRef<Map<string, string>>(new Map());
  const settingsSavedRef = useRef<string | null>(null);
  // Dirty state per scenario before its first Detailed calculations edit, so Discard can put it back.
  const dirtyBeforeTabEditsRef = useRef<Map<string, boolean>>(new Map());
  const hasHydratedRef = useRef(false);
  const [hydrationApplied, setHydrationApplied] = useState(false);

  // A hydrated, complete baseline shows the "Update forecast" state without a reload.
  useEffect(
    function promoteHasCalculatedForCompleteBaseline() {
      if (!hydrationApplied) return;
      // Leftover blank rows don't count, as in OutcomeComparison's readiness check.
      if (!referenceYearsAreComplete(withEmptyYearsPruned(scenarios))) return;
      setHasCalculated(true);
    },
    [hydrationApplied, scenarios]
  );

  const postHydrationLatch = usePostHydrationLatch();

  const autosaveRefs = useRef({ hasHydratedRef, cropYearsSavedRef, scenarioMetaSavedRef, settingsSavedRef }).current;

  // Set once the recalculation hook exists; the reset needs it but runs before that hook in render order.
  const clearOutcomesRef = useRef<() => void>(() => {});

  // Page state that belongs to the project being left; its results, banner and scroll intents must not land on the next one.
  const resetForProjectSwitch = useCallback(
    function resetForProjectSwitch() {
      setHasCalculated(false);
      setIsDirty(false);
      clearOutcomesRef.current();
      setDroppedMainCrops([]);
      setDroppedCropsBannerDismissed(false);
      postHydrationLatch.reset();
      scrollIntents.cancelAll();
    },
    [postHydrationLatch, scrollIntents]
  );

  const {
    soilSightProjectId,
    soilSightCacheReady,
    projectEpochRef,
    ensureProjectId,
    pageData,
    projectEntry,
    pendingDeleteIds,
    handleDeleteProject,
    reloadSoilSightProject,
    clearActiveProject,
    fieldReselectNeeded,
    openFieldReselect,
    closeFieldReselect,
  } = useActiveProjectLifecycle({
    klimFarmId,
    landing,
    autosaveRefs,
    hasHydratedRef,
    hydrationApplied,
    setHydrationApplied,
    onProjectReset: resetForProjectSwitch,
    showTransitionModal,
    setShowTransitionModal,
    setAwaitingInitialCalc,
    setDroppedMainCrops,
    openModal,
    closeModal,
    setSettings,
    setProjectRefinements,
    setScenarios,
    setHasCalculated,
    setIsDirty,
  });
  const {
    projectQuery,
    scenarioListQuery,
    projects,
    farmFields,
    farmFieldsFetching,
    refetchFarmFields,
    cropDefaultsByCropId,
    nonCropDefaults,
    mainCropLabelLookup,
    anyDetailLoading,
    anyDetailError,
    firstDetailError,
    allDetailsResolved,
    projectFieldId,
    fieldSoil,
    coverCropTiming,
    fieldsForProjectFarmLoaded,
  } = pageData;
  const {
    showProjectEntryDialog,
    addToExistingFlowRef,
    isEntryActionRunningRef,
    handleProjectEntryCreateNew,
    handleProjectEntryAddToExisting,
    handleProjectEntryClose,
  } = projectEntry;

  const { markScenarioChange, onScenarioMetaSaved, onCropYearsSaved } = useScenarioUpdateTracking();
  const persistence = useSoilSightPersistence({
    isDirty,
    soilSightProjectId,
    scenarios,
    setScenarios,
    settings,
    projectRefinements,
    ensureProjectId,
    projectEpochRef,
    autosaveRefs,
    onScenarioMetaSaved,
    onCropYearsSaved,
  });
  const { saveSettingsNow, setPendingSettingsPayload, flushUnsavedChanges, shouldBlockSwitchAfterFailedSave, pruneAndPersistEmptyYears } = persistence;
  const { projectHeaderVersion, saveBeforeLeavingProject, handleUserSwitchProject, handleUserProjectCreated } = useLeaveProjectWithFlush({
    hasHydratedRef,
    flushUnsavedChanges,
    shouldBlockSwitchAfterFailedSave,
    reloadSoilSightProject,
    isEntryActionRunningRef,
  });

  const handleRefine = useCallback(
    function handleRefine() {
      openModal("projectSettings");
    },
    [openModal]
  );
  const closeProjectSettings = useCallback(
    function closeProjectSettings() {
      closeModal("projectSettings");
    },
    [closeModal]
  );

  const revealFirstIncompleteScenario = useCallback(
    function revealFirstIncompleteScenario() {
      function showTheOffendingCard() {
        if (!scrollToFirstIncompleteScenario()) scrollToBuilder({ behavior: "instant", correct: true });
      }

      // We always call goToTab. A blocked run is often a tab switch that has not rendered yet.
      goToTabRef.current?.("scenarios", showTheOffendingCard);
    },
    [scrollToFirstIncompleteScenario, scrollToBuilder]
  );
  const recalcValidation = useRecalcValidation(revealFirstIncompleteScenario);
  const { recalcAttempted, flagIncompleteScenarios, clearValidationErrors, passesValidation } = recalcValidation;

  const {
    isCalculating,
    isSettingsRunPending,
    isRunRequested,
    soilSightOutcomes,
    keyInsight,
    currency,
    clearOutcomes,
    firstCalculationSettledRef,
    recalculateInFlightRef,
    isSavingCount,
    handleValidatedRecalculate,
    runSettingsRecalculate,
    recalculateAfterSettingsSave,
    calculationsAreStale,
    recalculateOnTabArrival,
    recalculateOnProjectLoad,
    recalculateAfterFieldReselect,
  } = useScenarioRecalc({
    scenarios,
    isDirty,
    setIsDirty,
    hasCalculated,
    setHasCalculated,
    awaitingInitialCalc,
    setAwaitingInitialCalc,
    projectQuery,
    ensureProjectId,
    projectEpochRef,
    persistence,
    recalcValidation,
    dirtyBeforeTabEditsRef,
    scrollIntents,
    fieldReselectNeeded,
    openFieldReselect,
    timelineYears: Number(settings.timeline),
  });

  clearOutcomesRef.current = clearOutcomes;
  // A run is out or about to start, so outcomes and the actions that would start another show as busy.
  const isBusy = isCalculating || awaitingInitialCalc;

  const detailedCalcsLeaveGuardRef = useRef<DetailedCalcsLeaveGuard | null>(null);
  const {
    activeTab,
    goToTab,
    changeDots,
    restoreChangeDots,
    isAwaitingRecalc,
    detailedCalcsScenarioId,
    setDetailedCalcsScenarioId,
    detailedCalcsView,
    setDetailedCalcsView,
    detailedCalcsIsEditing,
    setDetailedCalcsEditing,
    openDetailedCalculations,
  } = useScenarioAnalysisTabs({
    isDirty,
    isStale: calculationsAreStale,
    isCalculating,
    hasCalculatedOnceRef: firstCalculationSettledRef,
    recalculateInFlightRef,
    recalculate: recalculateOnTabArrival,
    leaveGuardRef: detailedCalcsLeaveGuardRef,
  });
  activeTabRef.current = activeTab;
  goToTabRef.current = goToTab;

  const handleSettingsChange = useCallback(
    function handleSettingsChange(newSettings: ScenarioSettings) {
      setSettings(newSettings);
      setIsDirty(true);
      if (activeTab === "scenarios") return;
      // The run starts before the render that would carry this pick into the flush.
      setPendingSettingsPayload(toApiSettingsRefinements({ settings: newSettings, projectRefinements }));
      runSettingsRecalculate();
    },
    [activeTab, projectRefinements, runSettingsRecalculate, setPendingSettingsPayload]
  );

  // The explainer drawer closes first so the Detailed calculations tab it hands over to is visible.
  const handleSetPricesAndCosts = useCallback(
    function handleSetPricesAndCosts() {
      closeModal("howThisWorks");
      openDetailedCalculations(undefined, { startEditing: true });
    },
    [closeModal, openDetailedCalculations]
  );

  const { handleScenariosChange, handleCreateScenario, handlePersistScenarioYears, handleEditCropYearField, handleDiscardCropYearEdits } = useScenarioEditing({
    scenarios,
    setScenarios,
    setIsDirty,
    isDirtyRef,
    dirtyBeforeTabEditsRef,
    autosaveRefs,
    projectEpochRef,
    postHydrationLatch,
    ensureProjectId,
    setAwaitingInitialCalc,
    setDroppedCropsBannerDismissed,
    scrollToScenarioCard,
    pruneAndPersistEmptyYears,
    passesValidation,
    restoreChangeDots,
    detailedCalcsIsEditing,
    markScenarioChange,
  });

  const handleFieldReplaced = useCallback(
    function handleFieldReplaced() {
      clearOutcomes();
      setIsDirty(true);
    },
    [clearOutcomes]
  );

  const { isConnectedFieldUsable, isSavingFieldReselect, handleFieldReselectConfirm, pendingReselectRecalcFieldRef } = useFieldReselect({
    klimFarmId,
    soilSightProjectId,
    fieldReselectNeeded,
    fieldsForProjectFarmLoaded,
    openReselectModal: openFieldReselect,
    closeReselectModal: closeFieldReselect,
    settings,
    projectRefinements,
    setProjectRefinements,
    saveSettingsNow,
    onFieldReplaced: handleFieldReplaced,
    postHydrationLatch,
    recalculate: recalculateAfterFieldReselect,
  });

  useAutoRecalcAfterHydration({
    soilSightProjectId,
    hydrationApplied,
    scenarios,
    cropYearsSavedRef,
    isSavingCount,
    farmFieldsFetching,
    fieldReselectNeeded,
    pendingReselectRecalcFieldRef,
    fieldImportAlreadyPersistedRef,
    transitionImportReadyForRecalc,
    isAwaitingProjectChoice: isAwaitingProjectChoice && !projectEntryDismissed,
    importedFieldCropYearsRef,
    addToExistingFlowRef,
    postHydrationLatch,
    markLandingRecalcPending: scrollIntents.markLandingRecalcPending,
    pruneAndPersistEmptyYears,
    recalculate: recalculateOnProjectLoad,
    setIsDirty,
    setAwaitingInitialCalc,
    flagIncompleteScenarios,
    revealFirstIncompleteScenario,
  });

  const handleProjectSettingsSave = useProjectSettingsSave({
    settings,
    setSettings,
    setProjectRefinements,
    setIsDirty,
    closeDrawer: closeProjectSettings,
    scenarios,
    persistence,
    passesValidation,
    recalculateAfterSettingsSave,
  });

  const handleNavigateToReference = useCallback(
    function handleNavigateToReference() {
      goToTab("scenarios", function scrollOnceBuilderIsPainted() {
        scrollToBuilder({ behavior: "smooth" });
      });
    },
    [goToTab, scrollToBuilder]
  );

  // After hydration and auto-recalc, as before the split: adopting another project must reset a stale load that just landed.
  useReconcileActiveProjectWithProjectList({
    klimFarmId,
    soilSightProjectId,
    projects,
    // A refetch in flight (e.g. right after a create) may still lack the newest project, so only a settled list is trusted.
    isProjectListLoaded: pageData.listProjectsQuery.isSuccess && !pageData.listProjectsQuery.isFetching,
    pendingDeleteIds,
    reloadSoilSightProject,
    clearActiveProject,
  });

  const navigationCrumbs = useMemo(
    () => ({
      crumbs: [{ name: t("scenarioBuilder.navBreadcrumb") }],
      additionalData: klimFarmId ? (
        <ProjectManagement
          key={`${soilSightProjectId ?? "no-project"}-${projectHeaderVersion}`}
          klimFarmId={klimFarmId}
          activeProjectId={soilSightProjectId}
          onSwitchProject={handleUserSwitchProject}
          onProjectCreated={handleUserProjectCreated}
          onBeforeDuplicateActiveProject={saveBeforeLeavingProject}
          pendingDeleteIds={pendingDeleteIds}
          onDeleteProject={handleDeleteProject}
        />
      ) : (
        <></>
      ),
    }),
    [
      klimFarmId,
      soilSightProjectId,
      projectHeaderVersion,
      handleUserSwitchProject,
      handleUserProjectCreated,
      saveBeforeLeavingProject,
      pendingDeleteIds,
      handleDeleteProject,
      t,
    ]
  );

  useEffect(
    function publishNavigationCrumbs() {
      if (!navigationCrumbs.crumbs.length) return;
      setNavigationCrumbs?.(navigationCrumbs);
    },
    [navigationCrumbs, setNavigationCrumbs]
  );

  const handleEditScenario = useCallback(
    function handleEditScenario(scenarioId: string) {
      // Editing instead of viewing the chart drops any pending chart landing.
      scrollIntents.cancelAll();
      goToTab("scenarios");
      // Lands on this specific card; scrollToScenarioCard waits two frames for the tab's layout to settle.
      scrollToScenarioCard(scenarioId);
    },
    [goToTab, scrollIntents, scrollToScenarioCard]
  );

  const handleEditBaseline = useCallback(
    function handleEditBaseline() {
      const referenceScenario = getReferenceScenario(scenarios);
      if (!referenceScenario) return;
      handleEditScenario(referenceScenario.id);
    },
    [scenarios, handleEditScenario]
  );

  const handleEditEconomicInputs = useCallback(
    function handleEditEconomicInputs() {
      const { selected } = resolveComparisonSelection(scenarios, comparisonScenarioId);
      openDetailedCalculations(selected?.id, { startEditing: true, returnTo: "worth-it" });
    },
    [scenarios, comparisonScenarioId, openDetailedCalculations]
  );

  const handleDetailedCalcsEditingChange = useCallback(
    function handleDetailedCalcsEditingChange(isEditing: boolean) {
      setDetailedCalcsEditing(isEditing, function scrollOnceOriginTabIsPainted() {
        scrollToEconomics("smooth");
      });
    },
    [setDetailedCalcsEditing, scrollToEconomics]
  );

  // The bar stays clickable during an edit. We finish the edit like Done does, or a later tab switch asks to discard the kept values.
  // Done runs the edits on arrival, but only after a first forecast settles.
  const handlePrimaryAction = useCallback(
    function handlePrimaryAction() {
      const runsOnArrival = detailedCalcsIsEditing && firstCalculationSettledRef.current;
      if (detailedCalcsIsEditing) handleDetailedCalcsEditingChange(false);
      if (!runsOnArrival) handleValidatedRecalculate();
    },
    [detailedCalcsIsEditing, firstCalculationSettledRef, handleDetailedCalcsEditingChange, handleValidatedRecalculate]
  );

  const handleViewResults = useCallback(
    function handleViewResults() {
      goToTab("worth-it", function scrollOnceVerdictTabIsPainted() {
        scrollToChart("smooth");
      });
    },
    [goToTab, scrollToChart]
  );

  const handleAddScenarioClick = useCallback(
    function handleAddScenarioClick() {
      if (scenarios.length >= SCENARIO_LIMITS.MAX_SCENARIOS) return;
      goToTab("scenarios");
      scenarioBuilderRef.current?.openAddScenarioModal();
    },
    [scenarios, goToTab]
  );

  const projectionYears = settings.timeline;

  const recalcValidationErrors = useMemo(() => (recalcAttempted ? getRecalcValidationErrors(scenarios) : undefined), [recalcAttempted, scenarios]);

  // Same "has a live, trustworthy calculation result" signal OutcomeComparison derives internally.
  const hasOutcomesData = !!soilSightOutcomes && Object.keys(soilSightOutcomes).length > 0;

  const handleTransitionExplore = useCallback(
    function handleTransitionExplore() {
      setShowTransitionModal(false);
      // Otherwise going back to this page (e.g. cancelling the PDF print) replays the import landing.
      clearImportRouterState();
      // Lands on the chart if the import's run already finished, else on the first incomplete scenario.
      scrollIntents.scrollToChartAfterRecalcRef.current = false;
      requestAnimationFrame(() => {
        // A run still in progress scrolls once it settles, so the chart is never shown loading.
        const shouldLandOnChart = hasCalculated && !isCalculating && areScenariosReadyForRecalculation(scenarios);
        if (shouldLandOnChart) scrollToChart("smooth");
        else revealFirstIncompleteScenario();
      });
    },
    [clearImportRouterState, hasCalculated, isCalculating, scrollIntents, scrollToChart, revealFirstIncompleteScenario, scenarios]
  );

  const { hasHardError, isHardLoading } = getPageLoadState({
    soilSightProjectId,
    soilSightCacheReady,
    hydrationApplied,
    projectQuery,
    scenarioListQuery,
    anyDetailLoading,
    anyDetailError,
    allDetailsResolved,
  });

  useFieldImportLandingScroll({
    scroll,
    isActiveRef: fieldImportAlreadyPersistedRef,
    showTransitionModal,
    isHardLoading,
    hydrationApplied,
    isCalculating,
    scrollToChartAfterRecalcRef: scrollIntents.scrollToChartAfterRecalcRef,
    landingRecalcPendingRef: scrollIntents.landingRecalcPendingRef,
  });

  useChartScrollOnRecalcStart({ scroll, isCalculating, scrollToChartOnStartRef: scrollIntents.scrollToChartOnStartRef });

  // Add scenario / Edit scenario drive the builder through its ref, so it stays mounted behind its tab.
  const builderSection = (
    <div ref={builderRef} className="scenario-analysis-builder-scroll-target" data-testid="scenario-analysis-builder-scroll-target">
      {showDroppedCropsBanner && (
        <AlertBanner
          dataTestId="dropped-crops-banner"
          type="error"
          iconName="tabler/alert-triangle"
          iconAlign="start"
          closeable
          onClose={() => setDroppedCropsBannerDismissed(true)}
          description={t("scenarioBuilder.droppedCropsBanner")}
          className="margin-bottom-150"
        />
      )}
      <ScenarioBuilder
        ref={scenarioBuilderRef}
        scenarios={scenarios}
        soilSightOutcomes={soilSightOutcomes}
        projectionYears={projectionYears}
        isStale={calculationsAreStale}
        onScenariosChange={handleScenariosChange}
        onCreateScenario={handleCreateScenario}
        onPersistScenarioYears={handlePersistScenarioYears}
        onScenarioChange={markScenarioChange}
        cropDefaultsByCropId={cropDefaultsByCropId}
        coverCropDefaults={nonCropDefaults?.coverCropDefaults ?? null}
        coverCropTiming={coverCropTiming ?? null}
        recalcValidationErrors={recalcValidationErrors}
        onClearValidationErrors={clearValidationErrors}
        onViewResults={handleViewResults}
      />
    </div>
  );

  // The chart, per-year table and financial breakdown.
  const resultsSection = (
    <div ref={chartRef}>
      <OutcomeComparison
        dataTestId="outcome-comparison"
        scenarios={scenarios}
        projectionYears={projectionYears}
        hasCalculated={hasCalculated}
        onNavigateToReference={handleNavigateToReference}
        isDirty={isDirty}
        onRecalculate={handleValidatedRecalculate}
        isCalculating={isCalculating}
        isAwaitingInitialCalculation={awaitingInitialCalc}
        soilSightOutcomes={soilSightOutcomes}
        marketPricePct={settings.market}
        costDevelopmentPct={settings.costs}
        onEditScenario={handleEditScenario}
        onOpenCalculations={() => openDetailedCalculations()}
        onHowIsThisCalculated={() => openModal("howThisWorks")}
        klimFarmId={klimFarmId}
        // The project-cache value the isFieldValid gate reads; local state lags a render and would fetch a deleted id (403).
        klimFieldId={projectFieldId}
        isFieldValid={isConnectedFieldUsable}
        viewRequest={chartViewRequest}
        onViewRequestHandled={handleChartViewRequestHandled}
        comparisonScenarioId={comparisonScenarioId}
        onViewModeChange={setActiveChartView}
      />
    </div>
  );

  // Shares the chart's query so both quote one score; gated to where the cards actually read it.
  const { resilienceData, isResilienceError, isResilienceFetching } = useScenarioResilienceScores({
    scenarios,
    projectionYears,
    klimFarmId,
    klimFieldId: projectFieldId,
    isFieldValid: isConnectedFieldUsable,
    enabled: !hasHardError,
  });

  const fieldName = farmFields.find((field) => field.id === projectFieldId)?.name ?? null;

  const handleDownloadPdf = useScenarioPdfExport({ scenarios, soilSightOutcomes, settings, currency, resilienceData, isResilienceError, fieldName });

  const sideBySideSection = (
    <SideBySideSection
      scenarios={scenarios}
      soilSightOutcomes={soilSightOutcomes}
      projectionYears={projectionYears}
      currency={currency}
      resilienceScores={resilienceData}
      isStale={calculationsAreStale}
      isCalculating={isBusy}
      onExplain={() => openModal("sideBySideExplainer")}
    />
  );

  const rotationNeedsAttention = !referenceIsReadyToCalculate(scenarios) || !areScenariosReadyForRecalculation(withEmptyYearsPruned(scenarios));

  const detailedCalculationsSection = (
    <DetailedCalculationsTab
      scenarios={scenarios}
      soilSightOutcomes={soilSightOutcomes}
      mainCropLabelLookup={mainCropLabelLookup}
      cropDefaultsByCropId={cropDefaultsByCropId}
      coverCropDefaults={nonCropDefaults?.coverCropDefaults ?? null}
      scenarioId={detailedCalcsScenarioId}
      onSelectScenario={setDetailedCalcsScenarioId}
      view={detailedCalcsView}
      onViewChange={setDetailedCalcsView}
      isEditing={detailedCalcsIsEditing}
      onEditingChange={handleDetailedCalcsEditingChange}
      onFieldChange={handleEditCropYearField}
      onDiscardEdits={handleDiscardCropYearEdits}
      leaveGuardRef={detailedCalcsLeaveGuardRef}
      isRecalculating={isBusy}
      isRunRequested={isRunRequested}
      isStale={calculationsAreStale}
      onRecalculate={handleValidatedRecalculate}
      needsRotationFix={rotationNeedsAttention}
      onFixRotation={revealFirstIncompleteScenario}
      marketPricePct={settings.market}
      costDevelopmentPct={settings.costs}
    />
  );

  const handleOpenChartView = useCallback(
    function handleOpenChartView(view: ViewMode, scenarioId?: string) {
      setChartViewRequest({ view, scenarioId });
      // The row is navigation, not a jump: with the chart already on screen, moving the page would be the only thing that changed.
      if (!isChartInView()) scrollToChart("smooth");
    },
    [scrollToChart, isChartInView]
  );

  const primaryAction = getForecastPrimaryAction({
    activeTab,
    isSettingsRunPending,
    isRecalculating: isBusy || isAwaitingRecalc,
    isDirty,
    hasCalculated,
    hasOutcomesData,
  });

  // Without a baseline rotation the action explains itself instead of running into validation errors.
  const primaryActionBlockedReason =
    primaryAction === "recalculate" && !referenceIsReadyToCalculate(scenarios) ? t("forecastSettingsBar.needsBaselineRotation") : null;

  return (
    <MainPage fullWidth pageName="scenario-analysis" navigationCrumbs={navigationCrumbs} share={{ title: t("meta:soilSight.title") }} loading={isHardLoading}>
      {hasHardError ? (
        <div className="scenario-analysis-error" data-testid="scenario-analysis-error">
          {getUserFacingApiErrorMessage(projectQuery.error ?? scenarioListQuery.error ?? firstDetailError, t("outcomeComparison.soilSightsUnavailable"))}
        </div>
      ) : (
        <>
          <ScenarioAnalysisView
            activeTab={activeTab}
            onTabChange={goToTab}
            changeDots={changeDots}
            settings={settings}
            onSettingsChange={handleSettingsChange}
            primaryAction={primaryAction}
            onPrimaryAction={handlePrimaryAction}
            primaryActionBlockedReason={primaryActionBlockedReason}
            onAddScenario={handleAddScenarioClick}
            onEditScenarios={handleEditBaseline}
            onMoreFilters={handleRefine}
            disableAddScenario={scenarios.length >= SCENARIO_LIMITS.MAX_SCENARIOS}
            scenarios={scenarios}
            soilSightOutcomes={soilSightOutcomes}
            projectionYears={projectionYears}
            currency={currency}
            resilienceScores={resilienceData}
            isStale={calculationsAreStale}
            isCalculating={isBusy}
            comparisonScenarioId={comparisonScenarioId}
            onSelectComparisonScenario={setComparisonScenarioId}
            onAddBaselineRotation={revealFirstIncompleteScenario}
            onRecalculate={handleValidatedRecalculate}
            onGetMoreKeyInsights={openKeyInsights}
            onOpenChartView={handleOpenChartView}
            activeChartView={activeChartView}
            fieldSoil={fieldSoil ?? null}
            onExplainFieldSoil={() => openModal("fieldSoilExplainer")}
            onEditEconomicInputs={handleEditEconomicInputs}
            economicsRef={economicsRef}
            onDownloadPdf={handleDownloadPdf}
            isFetchingResilience={isResilienceFetching}
            resultsSection={resultsSection}
            sideBySideSection={sideBySideSection}
            detailedCalculationsSection={detailedCalculationsSection}
            builderSection={builderSection}
          />

          <ScenarioAnalysisModals
            openModals={openModals}
            onCloseModal={closeModal}
            transition={{
              isVisible: showTransitionModal,
              onExplore: handleTransitionExplore,
              cropRotationLoaded: transitionCropRotationLoaded,
              calculationStepsVisible: transitionImportReadyForRecalc,
            }}
            projectEntry={{
              open: showProjectEntryDialog,
              projects,
              activeProjectId: soilSightProjectId,
              onCreateNew: handleProjectEntryCreateNew,
              onAddToExisting: handleProjectEntryAddToExisting,
              onClose: handleProjectEntryClose,
            }}
            projectSettings={{
              settings: drawerSettings,
              onSave: handleProjectSettingsSave,
              nonCropDefaults,
              fields: farmFields,
              onFieldAdded: refetchFarmFields,
            }}
            keyInsights={{
              scenarios,
              soilSightOutcomes,
              projectionYears,
              projectId: soilSightProjectId,
              keyInsight,
              currency,
              preselectedScenarioId: keyInsightsScenarioId,
            }}
            howThisWorks={{ onSetPricesAndCosts: handleSetPricesAndCosts }}
            fieldReselect={{ farmId: klimFarmId, saving: isSavingFieldReselect, onConfirm: handleFieldReselectConfirm }}
          />
        </>
      )}
    </MainPage>
  );
};

export default ScenarioAnalysis;
